/**
 * RescueTaxSimulatorV2 — UX SCAFFOLD, sin cálculo fiscal real.
 *
 * Propuesta de "Definimos MVP rescates" (Notion), pieza 4: calculadora de
 * impacto fiscal al rescatar (capital vs. renta vs. mixto).
 *
 * Referencia competitiva (investigación manual sobre Santander, BBVA AM,
 * VidaCaixa/Aportamas, Instituto Santalucía y Fundación Mapfre, jul-2026):
 * el diferenciador real no es la fiscalidad exacta (nadie la reproduce al
 * 100% sin conectar con la operación SIMU de Caser), sino EXPLICAR el
 * "porqué" del resultado — de ahí el bloque de tipo medio/marginal de
 * partida (idea de Instituto Santalucía, el más transparente de los 5) y las
 * notas educativas de cada modalidad.
 *
 * Simplificación (feedback de usuario, jul-2026): en vez de comparar las 3
 * modalidades a la vez, primero se elige el TIPO de rescate (Total, Renta
 * mensual, Mixto) y solo se muestra el resultado de esa modalidad — el
 * "recomendado" no desaparece, se comunica como delta ("pagarías X€ más que
 * con la opción recomendada") en vez de como 3 cards lado a lado. Para renta
 * y mixto, el control ya no es "cuántos años quieres repartirlo" sino
 * "cuánto quieres cobrar al mes" (mismo modelo mental que RescueRentaSimulatorV2),
 * y la duración se calcula como resultado, no se pide como dato.
 *
 * ⚠️ NO SEGUIR ESTE ARCHIVO COMO REFERENCIA DE CALIDAD: el cálculo de IRPF de
 * abajo es una aproximación (escala general del Estado real + un ajuste
 * autonómico orientativo por tramo, no la escala oficial de cada CCAA) y un
 * mínimo personal simplificado — no una reducción por rendimientos del
 * trabajo completa. Le faltan, a propósito: tests (TDD estricto, ver
 * CLAUDE.md), y el cálculo real — que debería venir de la operación `SIMU`
 * de Caser (nunca llamada hoy, ver tarea Notion) en vez de reconstruirlo aquí
 * a mano.
 */
import React, { useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Info, Wallet, TrendingUp, PiggyBank, Sparkles, AlertTriangle } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import ResponsiveTooltip from '../ui/responsive-tooltip';
import { fmtEur } from '../../utils/formatArcaNumbers';
import { I18nContext } from '../../context/translations/TranslationsContext.context';

export interface RescueTaxSimulatorV2Props {
	// Importe rescatable real del contexto desde el que se abre (María en su
	// hero, Ricardo en su card de antigüedad...). Si no se pasa, se usa un
	// importe de ejemplo para que la calculadora abierta "a pelo" desde
	// Simuladores no se vea vacía.
	initialAvailable?: number;
	// Título de la card de inputs. Por defecto "Calculadora de rescate"; los
	// sitios que ya envuelven esto en su propio Dialog con ese mismo título
	// visible (usuarios con rescate activo) pasan "Simulador" para no duplicarlo.
	cardTitle?: string;
}

type RescueMode = 'total' | 'renta' | 'mixto';

// Escala GENERAL del Estado 2025/2026 — esta parte SÍ es real (igual en todo
// el territorio común, fuera de País Vasco/Navarra).
const STATE_BRACKETS = [
	{ upTo: 12450, rate: 0.19 },
	{ upTo: 20200, rate: 0.24 },
	{ upTo: 35200, rate: 0.3 },
	{ upTo: 60000, rate: 0.37 },
	{ upTo: 300000, rate: 0.45 },
	{ upTo: Infinity, rate: 0.47 }
];

// Mínimo personal general (<65 años, sin descendientes/discapacidad) — se
// resta de la base antes de aplicar tramos. Simplificación: en la realidad
// también hay reducción por rendimientos del trabajo (se va agotando a
// partir de ~14.850€ de rendimiento neto y desaparece sobre los ~21.800€),
// que no modelamos aquí porque pesa menos que el propio salto de tramo.
const PERSONAL_MINIMUM = 5550;

// Ajuste autonómico por tramo (mismos cortes que STATE_BRACKETS) — NO son los
// tramos oficiales de cada CCAA, es una aproximación de "cuánto más/menos
// paga cada comunidad frente a la media" que crece con la renta (así es como
// se comportan las escalas autonómicas reales: la brecha entre CCAA es
// pequeña en los tramos bajos y se abre en los altos). Fuente: posición
// relativa conocida de cada comunidad, no la escala BOE/BOCA de cada año.
const REGIONAL_ADJUST: Record<string, number[]> = {
	madrid: [-0.005, -0.01, -0.015, -0.02, -0.02, -0.02],
	catalunya: [0, 0.005, 0.01, 0.02, 0.025, 0.03],
	andalucia: [-0.005, -0.005, -0.005, -0.01, -0.01, -0.01],
	valencia: [0, 0, 0.01, 0.02, 0.03, 0.035],
	'pais-vasco': [-0.01, -0.005, 0, 0.01, 0.015, 0.02],
	galicia: [-0.005, -0.005, -0.005, -0.01, -0.01, -0.01],
	otras: [0, 0, 0, 0, 0, 0]
};

const CCAA_OPTIONS: { value: string; labelKey: string }[] = [
	{ value: 'madrid', labelKey: 'rescue.taxSimulator.regionMadrid' },
	{ value: 'catalunya', labelKey: 'rescue.taxSimulator.regionCatalunya' },
	{ value: 'andalucia', labelKey: 'rescue.taxSimulator.regionAndalucia' },
	{ value: 'valencia', labelKey: 'rescue.taxSimulator.regionValencia' },
	{ value: 'pais-vasco', labelKey: 'rescue.taxSimulator.regionPaisVasco' },
	{ value: 'galicia', labelKey: 'rescue.taxSimulator.regionGalicia' },
	{ value: 'otras', labelKey: 'rescue.taxSimulator.regionOtras' }
];

const MIN_RENTA_YEARS = 1;
const MAX_RENTA_YEARS = 40;
const MIN_MONTHLY_AMOUNT = 50;

function bracketRates(regionAdjust: number[]): number[] {
	return STATE_BRACKETS.map((b, i) => Math.max(b.rate + (regionAdjust[i] ?? 0), 0));
}

// Cuota íntegra sobre `income`, tras restar el mínimo personal.
function progressiveTax(income: number, regionAdjust: number[]): number {
	const taxable = Math.max(income - PERSONAL_MINIMUM, 0);
	if (taxable <= 0) return 0;
	const rates = bracketRates(regionAdjust);
	let tax = 0;
	let prev = 0;
	for (let i = 0; i < STATE_BRACKETS.length; i++) {
		if (taxable <= prev) break;
		const taxableInBracket = Math.min(taxable, STATE_BRACKETS[i].upTo) - prev;
		tax += taxableInBracket * rates[i];
		prev = STATE_BRACKETS[i].upTo;
	}
	return tax;
}

// Tipo marginal (el tramo donde cae el último euro de `income`, ya
// descontado el mínimo personal) — para explicar "a qué tipo tributa lo
// último que sumas", no el tipo medio de toda la base.
function marginalRateAt(income: number, regionAdjust: number[]): number {
	const taxable = Math.max(income - PERSONAL_MINIMUM, 0);
	const rates = bracketRates(regionAdjust);
	for (let i = 0; i < STATE_BRACKETS.length; i++) {
		if (taxable <= STATE_BRACKETS[i].upTo || i === STATE_BRACKETS.length - 1) return rates[i];
	}
	return rates[rates.length - 1];
}

// IRPF marginal que genera `extraIncome` al sumarse a `baseIncome` ya existente.
function marginalTax(baseIncome: number, extraIncome: number, regionAdjust: number[]): number {
	return (
		progressiveTax(baseIncome + extraIncome, regionAdjust) -
		progressiveTax(baseIncome, regionAdjust)
	);
}

// Cuántos años dura `pot` cobrando `monthly` al mes — el dato que antes se
// pedía como input ("años de renta") ahora es un resultado: lo natural es
// pensar en "cuánto quiero cobrar al mes", no en "cuántos años quiero que dure".
function yearsFromMonthly(pot: number, monthly: number): number {
	if (monthly <= 0) return MAX_RENTA_YEARS;
	return Math.min(MAX_RENTA_YEARS, Math.max(MIN_RENTA_YEARS, Math.round(pot / monthly / 12)));
}

const MODE_OPTIONS: { value: RescueMode; labelKey: string; icon: typeof Wallet }[] = [
	{ value: 'total', labelKey: 'rescue.taxSimulator.modeTotal', icon: Wallet },
	{ value: 'renta', labelKey: 'rescue.taxSimulator.modeRenta', icon: TrendingUp },
	{ value: 'mixto', labelKey: 'rescue.taxSimulator.modeMixto', icon: PiggyBank }
];

const RescueTaxSimulatorV2: React.FC<RescueTaxSimulatorV2Props> = ({
	initialAvailable,
	cardTitle
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const resolvedCardTitle = cardTitle ?? translate('rescue.taxSimulator.defaultCardTitle');
	const [rescueMode, setRescueMode] = useState<RescueMode>('total');
	// Sin saldo real se arranca en 0 y la persona mueve el slider: poner el del
	// caso de referencia le enseñaba a cualquiera un importe que no es el suyo.
	const [totalSaved, setTotalSaved] = useState(initialAvailable ?? 0);
	const [rescuePct, setRescuePct] = useState(100);
	const [ccaa, setCcaa] = useState('madrid');
	const [otherIncome, setOtherIncome] = useState(0);
	const [pre2007Amount, setPre2007Amount] = useState(0);
	// Cuánto quiere cobrar al mes en renta/mixto — la duración se deriva de
	// esto, no al revés.
	const [monthlyRentaAmount, setMonthlyRentaAmount] = useState(200);
	const [mixtoCapitalPct, setMixtoCapitalPct] = useState(40);

	const regionAdjust = REGIONAL_ADJUST[ccaa] ?? REGIONAL_ADJUST.otras;
	const rescueAmount = (totalSaved * rescuePct) / 100;
	// Reparto del mixto en euros — se usa tanto en los sliders (nunca hablamos
	// de porcentajes de cara al usuario, siempre cantidades) como en el cálculo.
	const capitalPart = (rescueAmount * mixtoCapitalPct) / 100;
	const rentaPart = rescueAmount - capitalPart;

	// Punto de partida ANTES de sumar el rescate — para poder explicar "ya
	// estás en el tramo X antes de rescatar nada" (idea de Instituto
	// Santalucía: el único de los simuladores investigados que lo muestra).
	const baseline = useMemo(() => {
		const tax = progressiveTax(otherIncome, regionAdjust);
		return {
			tax,
			avgRate: otherIncome > 0 ? (tax / otherIncome) * 100 : 0,
			marginalRate: marginalRateAt(otherIncome, regionAdjust) * 100
		};
	}, [otherIncome, regionAdjust]);

	const comparison = useMemo(() => {
		// Total: todo de golpe, en el ejercicio actual. La reducción del 40%
		// (aportaciones pre-2007) solo aplica al cobro en forma de capital.
		const reducibleCapital = Math.min(pre2007Amount, rescueAmount);
		const taxableCapital = rescueAmount - reducibleCapital * 0.4;
		const taxCapital = marginalTax(otherIncome, taxableCapital, regionAdjust);
		const netCapital = rescueAmount - taxCapital;
		const marginalCapital = marginalRateAt(otherIncome + taxableCapital, regionAdjust) * 100;

		// Renta: repartido en los años que salgan de "cuánto quiero cobrar al
		// mes" — sin reducción del 40%.
		const rentaYearsFull = yearsFromMonthly(rescueAmount, monthlyRentaAmount);
		const perYearRenta = rescueAmount / rentaYearsFull;
		const taxPerYearRenta = marginalTax(otherIncome, perYearRenta, regionAdjust);
		const taxRenta = taxPerYearRenta * rentaYearsFull;
		const netRenta = rescueAmount - taxRenta;
		const marginalRenta = marginalRateAt(otherIncome + perYearRenta, regionAdjust) * 100;
		const netRentaMonthly = netRenta / rentaYearsFull / 12;

		// Mixto: parte en capital (con reducción si aplica) + resto en renta,
		// también al ritmo de "cuánto quiero cobrar al mes".
		const mixtoRentaYears = yearsFromMonthly(rentaPart, monthlyRentaAmount);
		const reducibleMixto = Math.min(pre2007Amount, capitalPart);
		const taxableMixtoCapital = capitalPart - reducibleMixto * 0.4;
		const taxMixtoCapital = marginalTax(otherIncome, taxableMixtoCapital, regionAdjust);
		const perYearMixtoRenta = rentaPart / mixtoRentaYears;
		const taxMixtoRenta =
			marginalTax(otherIncome, perYearMixtoRenta, regionAdjust) * mixtoRentaYears;
		const taxMixto = taxMixtoCapital + taxMixtoRenta;
		const netMixto = rescueAmount - taxMixto;
		const netCapitalPart = capitalPart - taxMixtoCapital;
		const netMixtoRentaMonthly = (rentaPart - taxMixtoRenta) / mixtoRentaYears / 12;

		const rows = [
			{
				key: 'total' as RescueMode,
				label: translate('rescue.taxSimulator.rowLabelTotal'),
				tax: taxCapital,
				net: netCapital,
				mainValue: fmtEur(netCapital),
				subDetail: '',
				tipoMedio: rescueAmount > 0 ? (taxCapital / rescueAmount) * 100 : 0,
				tipoMarginal: marginalCapital,
				years: null as number | null,
				nota:
					reducibleCapital > 0
						? translate('rescue.taxSimulator.notaTotalWithReduction').replace(
								'{{amount}}',
								fmtEur(reducibleCapital)
							)
						: translate('rescue.taxSimulator.notaTotalNoReduction')
			},
			{
				key: 'renta' as RescueMode,
				label: translate('rescue.taxSimulator.rowLabelRenta').replace(
					'{{years}}',
					String(rentaYearsFull)
				),
				tax: taxRenta,
				net: netRenta,
				mainValue: `${fmtEur(netRentaMonthly)}/mes`,
				subDetail: '',
				tipoMedio: rescueAmount > 0 ? (taxRenta / rescueAmount) * 100 : 0,
				tipoMarginal: marginalRenta,
				years: rentaYearsFull,
				nota: translate('rescue.taxSimulator.notaRenta')
					.replace('{{monthly}}', fmtEur(monthlyRentaAmount))
					.replace('{{amount}}', fmtEur(rescueAmount))
					.replace('{{years}}', String(rentaYearsFull))
					.replace('{{perYear}}', fmtEur(perYearRenta))
			},
			{
				key: 'mixto' as RescueMode,
				label: translate('rescue.taxSimulator.rowLabelMixto')
					.replace('{{capital}}', fmtEur(capitalPart))
					.replace('{{renta}}', fmtEur(rentaPart)),
				tax: taxMixto,
				net: netMixto,
				mainValue: fmtEur(netCapitalPart),
				subDetail: `+ ${fmtEur(netMixtoRentaMonthly)}/mes`,
				tipoMedio: rescueAmount > 0 ? (taxMixto / rescueAmount) * 100 : 0,
				tipoMarginal: Math.max(marginalCapital, marginalRenta),
				years: mixtoRentaYears,
				nota: translate('rescue.taxSimulator.notaMixto')
					.replace('{{capital}}', fmtEur(capitalPart))
					.replace('{{renta}}', fmtEur(rentaPart))
					.replace('{{monthly}}', fmtEur(monthlyRentaAmount))
					.replace('{{years}}', String(mixtoRentaYears))
			}
		];

		const cheapestKey = rows.reduce((min, r) => (r.tax < min.tax ? r : min), rows[0]).key;
		return { rows, cheapestKey };
	}, [
		rescueAmount,
		otherIncome,
		regionAdjust,
		pre2007Amount,
		monthlyRentaAmount,
		capitalPart,
		rentaPart,
		translate
	]);

	const icons = { total: Wallet, renta: TrendingUp, mixto: PiggyBank };

	const selectedRow = comparison.rows.find((r) => r.key === rescueMode)!;
	const cheapestRow = comparison.rows.find((r) => r.key === comparison.cheapestKey)!;
	// No comparar por key (rescueMode === cheapestKey): si hay empate (p. ej.
	// las 3 modalidades pagan 0€ porque no llega al mínimo exento), reduce()
	// solo se queda con la PRIMERA en empatar y el resto quedarían marcadas
	// como "no recomendadas" sin serlo — comparar por importe evita ese falso
	// aviso ("pagarías 0,00€... prueba renta o mixto para reducir impuestos").
	const isRecommended = selectedRow.tax <= cheapestRow.tax;

	// Pote y tope del slider "cuánto quieres cobrar al mes" según el modo: en
	// mixto, la renta sale solo de lo que queda tras el tramo de golpe.
	const rentaPot =
		rescueMode === 'mixto' ? rescueAmount * (1 - mixtoCapitalPct / 100) : rescueAmount;
	const monthlySliderMax = Math.max(Math.ceil(rentaPot / 12 / 10) * 10, MIN_MONTHLY_AMOUNT * 2);

	const SelectedIcon = icons[selectedRow.key];

	// Compartido entre renta (a todo el ancho) y mixto (a dos columnas junto al
	// slider de golpe) — mismo control, distinto layout según el modo.
	const monthlyAmountControl = (
		<div className="space-y-1.5">
			<div className="flex items-center justify-between">
				<Label>{translate('rescue.taxSimulator.monthlyAmountLabel')}</Label>
				<span className="text-sm font-semibold text-primary">{fmtEur(monthlyRentaAmount)}</span>
			</div>
			<Slider
				className="slider-arca"
				value={[monthlyRentaAmount]}
				onValueChange={([v]) => setMonthlyRentaAmount(v)}
				min={MIN_MONTHLY_AMOUNT}
				max={monthlySliderMax}
				step={10}
			/>
			<p className="text-xs text-muted-foreground">
				{translate('rescue.taxSimulator.durationNotePrefix')}{' '}
				{rescueMode === 'mixto'
					? translate('rescue.taxSimulator.durationNoteMixtoSubject')
					: translate('rescue.taxSimulator.durationNoteTotalSubject')}{' '}
				{translate('rescue.taxSimulator.durationNoteSuffix')}{' '}
				<span className="font-semibold text-foreground">
					{selectedRow.years}{' '}
					{selectedRow.years === 1
						? translate('rescue.taxSimulator.yearSingular')
						: translate('rescue.taxSimulator.yearPlural')}
				</span>
				.
			</p>
		</div>
	);

	// Alto reservado = el que ocupa el contenido real cuando está seleccionado
	// "Mixto" (la variante más alta) — medido, no adivinado a ojo, para no
	// quedarnos cortos ni de más. Se aplica al contenedor de fuera, nunca a las
	// cards en sí: así el modal no cambia de tamaño al cambiar de pestaña, pero
	// ninguna card se estira ni deja huecos en blanco dentro de su borde.
	const contentRef = useRef<HTMLDivElement>(null);
	const [reservedHeight, setReservedHeight] = useState<number | undefined>(undefined);

	useLayoutEffect(() => {
		if (rescueMode === 'mixto' && contentRef.current) {
			const measured = contentRef.current.scrollHeight;
			setReservedHeight((prev) => (prev == null ? measured : Math.max(prev, measured)));
		}
	});

	return (
		<div className="individual-v2" style={{ minHeight: reservedHeight }}>
			<div ref={contentRef} className="space-y-5">
				{/* Las tabs van a todo el ancho del modal/componente, no solo del
			    ancho de la card del simulador. */}
				<Tabs value={rescueMode} onValueChange={(v) => setRescueMode(v as RescueMode)}>
					{/* 🔴 `flex-wrap` y alto automático: sin esto la tira EMPUJABA la página.
					    Los `TabsTrigger` son ítems flex, y su `min-width` por defecto es `auto`,
					    así que `flex-1` reparte el espacio pero no deja encoger por debajo del
					    contenido. En 390 px las tres pestañas con sus badges sumaban 416 px
					    dentro de una caja de 358, y esos 58 px de sobra llevaban el `<html>` a
					    432: aparecía scroll horizontal y el menú inferior se salía de la
					    pantalla, porque mide el 100% del documento.

					    Envuelve en vez de abreviar el texto a propósito: cualquier arreglo que
					    dependa de lo que midan las cadenas se rompe con otra traducción — el
					    badge en inglés es más largo. Y envuelve en vez de scrollear porque un
					    scroll dejaría una pestaña fuera de vista sin ninguna señal, y la red
					    visual no la vería: fotografía el estado inicial, no lo que hay tras
					    pulsar. */}
					<TabsList className="bg-muted/50 w-full flex-wrap h-auto min-h-10">
						{MODE_OPTIONS.map((opt) => {
							const Icon = opt.icon;
							return (
								<TabsTrigger key={opt.value} value={opt.value} className="gap-1.5 flex-1">
									<Icon className="w-3.5 h-3.5" aria-hidden="true" />
									{translate(opt.labelKey)}
									{opt.value !== 'total' && (
										<span className="ml-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary normal-case tracking-normal">
											{translate('rescue.taxSimulator.irpfBadge')}
										</span>
									)}
								</TabsTrigger>
							);
						})}
					</TabsList>
				</Tabs>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					{/* El simulador (2/3) y "Por qué..." (1/3) — mismo grid row, así
				    que el grid las estira a la misma altura. */}
					<div className="lg:col-span-2">
						{/* Una sola card: los inputs y el resultado de tocarlos viven
					    juntos, no en dos columnas separadas. */}
						<div className="surface-card p-5 sm:p-6 space-y-5">
							<h3 className="font-heading font-semibold">{resolvedCardTitle}</h3>

							<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
								<div className="space-y-1">
									<div className="flex items-center gap-1 h-4">
										<Label htmlFor="rescue-total-saved">
											{translate('rescue.taxSimulator.totalSavedLabel')}
										</Label>
									</div>
									<Input
										id="rescue-total-saved"
										type="number"
										min={0}
										value={totalSaved}
										onChange={(e) => setTotalSaved(Math.max(0, Number(e.target.value) || 0))}
									/>
								</div>
								<div className="space-y-1">
									<div className="flex items-center gap-1 h-4">
										<Label>{translate('rescue.taxSimulator.regionLabel')}</Label>
										<ResponsiveTooltip
											content={translate('rescue.taxSimulator.regionTooltip')}
											side="top"
										>
											<button type="button" className="inline-flex cursor-help">
												<Info className="w-3.5 h-3.5 text-muted-foreground" />
											</button>
										</ResponsiveTooltip>
									</div>
									<Select value={ccaa} onValueChange={setCcaa}>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{CCAA_OPTIONS.map((c) => (
												<SelectItem key={c.value} value={c.value}>
													{translate(c.labelKey)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-1">
									<div className="flex items-center gap-1 h-4">
										<Label htmlFor="rescue-other-income" className="whitespace-nowrap">
											{translate('rescue.taxSimulator.otherIncomeLabel')}
										</Label>
										<ResponsiveTooltip
											content={translate('rescue.taxSimulator.otherIncomeTooltip')}
											side="top"
										>
											<button type="button" className="inline-flex cursor-help">
												<Info className="w-3.5 h-3.5 text-muted-foreground" />
											</button>
										</ResponsiveTooltip>
									</div>
									<Input
										id="rescue-other-income"
										type="number"
										min={0}
										value={otherIncome}
										onChange={(e) => setOtherIncome(Math.max(0, Number(e.target.value) || 0))}
									/>
								</div>
								<div className="space-y-1">
									<div className="flex items-center gap-1 h-4">
										<Label htmlFor="rescue-pre-2007" className="whitespace-nowrap">
											{translate('rescue.taxSimulator.pre2007Label')}
										</Label>
										<ResponsiveTooltip
											content={translate('rescue.taxSimulator.pre2007Tooltip')}
											side="top"
										>
											<button type="button" className="inline-flex cursor-help">
												<Info className="w-3.5 h-3.5 text-muted-foreground" />
											</button>
										</ResponsiveTooltip>
									</div>
									<Input
										id="rescue-pre-2007"
										type="number"
										min={0}
										value={pre2007Amount}
										onChange={(e) => setPre2007Amount(Math.max(0, Number(e.target.value) || 0))}
									/>
								</div>
							</div>

							{otherIncome > 0 && (
								<div className="rounded-lg bg-muted/50 p-3 flex items-start gap-2">
									<TrendingUp className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
									<p className="text-xs text-muted-foreground">
										{
											translate('rescue.taxSimulator.baselineNote')
												.replace('{{income}}', fmtEur(otherIncome))
												.split('{{rate}}')[0]
										}
										<span className="font-semibold text-foreground">
											{baseline.marginalRate.toFixed(0)}%
										</span>
										{
											translate('rescue.taxSimulator.baselineNote')
												.replace('{{income}}', fmtEur(otherIncome))
												.split('{{rate}}')[1]
										}
									</p>
								</div>
							)}

							<div className="space-y-1.5">
								<div className="flex items-center justify-between">
									<Label>{translate('rescue.taxSimulator.amountLabel')}</Label>
									<span className="text-sm font-semibold text-primary">{fmtEur(rescueAmount)}</span>
								</div>
								<Slider
									className="slider-arca"
									value={[rescuePct]}
									onValueChange={([v]) => setRescuePct(v)}
									min={1}
									max={100}
									step={1}
								/>
							</div>

							<div className="space-y-5">
								{rescueMode === 'mixto' && (
									<div className="space-y-1.5">
										<div className="flex items-center justify-between">
											<Label>{translate('rescue.taxSimulator.lumpSumSliderLabel')}</Label>
											<span className="text-sm font-semibold text-primary">
												{fmtEur(capitalPart)}
											</span>
										</div>
										<Slider
											className="slider-arca"
											value={[mixtoCapitalPct]}
											onValueChange={([v]) => setMixtoCapitalPct(v)}
											min={10}
											max={90}
											step={5}
										/>
										<p className="text-xs text-muted-foreground">
											{translate('rescue.taxSimulator.mixtoSliderNote')
												.replace('{{capital}}', fmtEur(capitalPart))
												.replace('{{renta}}', fmtEur(rentaPart))}
										</p>
									</div>
								)}

								{(rescueMode === 'renta' || rescueMode === 'mixto') && monthlyAmountControl}
							</div>

							<hr className="border-border" />

							{/* RESULTADO — resultado de tocar la calculadora, en la misma card
				    que los inputs (no una columna aparte): solo la modalidad elegida,
				    con el delta vs. la recomendada en vez de comparar las 3 a la vez.
				    Compacto a propósito: no debe pesar más que el propio formulario. */}
							<div className="space-y-2.5">
								<div className="rounded-xl bg-primary/5 border border-primary/20 p-4 space-y-2.5">
									<div className="flex items-center gap-3">
										<div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
											<SelectedIcon className="w-4 h-4 text-primary" />
										</div>
										<div className="flex-1 min-w-0">
											<p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide truncate">
												{selectedRow.label}
											</p>
											<div className="flex items-baseline gap-2 flex-wrap">
												<p className="text-xl font-bold font-heading text-primary tracking-tight">
													{selectedRow.mainValue}
												</p>
												{selectedRow.subDetail && (
													<p className="text-xs text-muted-foreground truncate">
														{selectedRow.subDetail}
													</p>
												)}
											</div>
										</div>
									</div>

									<div className="pt-2.5 border-t border-primary/20">
										{selectedRow.tax === 0 ? (
											<p className="text-xs text-muted-foreground">
												<span className="font-semibold text-primary">
													{translate('rescue.taxSimulator.noIrpf')}
												</span>{' '}
												{translate('rescue.taxSimulator.noIrpfNote')}
											</p>
										) : (
											<p className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
												<span>
													<span className="font-semibold text-foreground">
														{fmtEur(selectedRow.tax)}
													</span>
													{translate('rescue.taxSimulator.irpfSuffix')}
												</span>
												<span className="flex items-center gap-0.5">
													<span className="font-semibold text-foreground">
														{selectedRow.tipoMedio.toFixed(1)}%
													</span>{' '}
													{translate('rescue.taxSimulator.avgLabel')}
													<ResponsiveTooltip
														content={translate('rescue.taxSimulator.avgTooltip')}
														side="top"
													>
														<button type="button" className="inline-flex cursor-help shrink-0">
															<Info className="w-3 h-3 text-muted-foreground" />
														</button>
													</ResponsiveTooltip>
												</span>
												<span className="flex items-center gap-0.5">
													<span className="font-semibold text-foreground">
														{selectedRow.tipoMarginal.toFixed(0)}%
													</span>{' '}
													{translate('rescue.taxSimulator.marginalLabel')}
													<ResponsiveTooltip
														content={translate('rescue.taxSimulator.marginalTooltip')}
														side="top"
													>
														<button type="button" className="inline-flex cursor-help shrink-0">
															<Info className="w-3 h-3 text-muted-foreground" />
														</button>
													</ResponsiveTooltip>
												</span>
											</p>
										)}
									</div>
								</div>

								{/* Delta vs. la opción más eficiente — así se sabe en qué casos no
					    se paga más IRPF que "el recomendado" aunque solo se vea 1 resultado. */}
								{isRecommended ? (
									<p className="text-[11px] text-foreground flex items-start gap-1.5 bg-primary/5 border border-primary/20 rounded-lg p-2.5">
										<Sparkles className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
										{translate('rescue.taxSimulator.recommendedNote')}
									</p>
								) : (
									<p className="text-[11px] text-foreground flex items-start gap-1.5 bg-[#fff8ec] border border-[#f5deb3] rounded-lg p-2.5">
										<AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
										<span>
											{translate('rescue.taxSimulator.notRecommendedPrefix').replace(
												'{{amount}}',
												fmtEur(selectedRow.tax)
											)}{' '}
											<span className="font-semibold">
												{translate('rescue.taxSimulator.notRecommendedSuffix')}
											</span>
										</span>
									</p>
								)}

								<p className="text-[10px] text-muted-foreground flex items-start gap-1.5">
									<Info className="w-3 h-3 mt-0.5 shrink-0" />
									{translate('rescue.taxSimulator.disclaimerNote')}
								</p>
							</div>
						</div>
					</div>

					{/* 1/3: la explicación, aparte del simulador. */}
					<div className="lg:col-span-1 space-y-6">
						<div className="surface-card p-5 sm:p-6 space-y-3">
							<h3 className="font-heading font-semibold">
								{translate('rescue.taxSimulator.whyTitle')}
							</h3>
							<div>
								<p className="text-xs font-semibold text-foreground mb-1">{selectedRow.label}</p>
								<p className="text-[11px] text-muted-foreground">{selectedRow.nota}</p>
							</div>
							{rescueMode === 'total' && (
								<div>
									<p className="text-xs font-semibold text-foreground mb-1">
										{translate('rescue.taxSimulator.whyNotAllAtOnceTitle')}
									</p>
									<p className="text-[11px] text-muted-foreground">
										{translate('rescue.taxSimulator.whyNotAllAtOnceBody')}
									</p>
								</div>
							)}
							<div>
								<p className="text-xs font-semibold text-foreground mb-1">
									{translate('rescue.taxSimulator.whyRegionTitle')}
								</p>
								<p className="text-[11px] text-muted-foreground">
									{translate('rescue.taxSimulator.whyRegionBody')}
								</p>
							</div>
						</div>

						<div className="surface-card p-5 sm:p-6 space-y-3">
							<h3 className="font-heading font-semibold flex items-center gap-2">
								<AlertTriangle className="w-4 h-4 text-muted-foreground shrink-0" />
								{translate('rescue.taxSimulator.importantTitle')}
							</h3>
							<p className="text-[11px] text-muted-foreground">
								{translate('rescue.taxSimulator.importantBody')}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default RescueTaxSimulatorV2;
